import { Component, OnInit, EventEmitter, Output, ViewChildren, QueryList, OnDestroy, Input } from '@angular/core';
import { IonicModule, MenuController, ToastController } from '@ionic/angular';
import { FilterInputComponent } from '../filter-input/filter-input.component';
import { CategoriesService } from 'src/app/services/api/categories.service';
import { TranslateConfigService } from 'src/app/services/translate-config.service';
import { AppStorageService } from '../../services/app-storage.service';
import { StorageKeys } from '../../services/storage-keys';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, TranslateModule, FilterInputComponent]
})
export class FiltersComponent implements OnInit, OnDestroy {
  @Output() filtersApplied = new EventEmitter<any>();
  @Input() isWeb: boolean = false;

  // Añadir ViewChildren para obtener todas las referencias a los componentes filter-input
  @ViewChildren(FilterInputComponent) filterInputs!: QueryList<FilterInputComponent>;

  // Objeto para almacenar los valores seleccionados
  selectedFilters: any = {};

  // Contador de filtros activos
  activeFiltersCount: number = 0;

  // Propiedades para manejar categorías y subcategorías
  categories: { id: number, name: string }[] = [];
  subcategories: { id: number, name: string }[] = [];
  isLoadingCategories = false;
  isLoadingSubcategories = false;
  categoryError: string | null = null;
  subcategoryError: string | null = null;

  constructor(
    private menuCtrl: MenuController,
    private categoriesService: CategoriesService,
    private translateConfigService: TranslateConfigService,
    private toastController: ToastController,
    private storage: AppStorageService
  ) { }

  ngOnInit() {
    // Intentar cargar filtros guardados previamente
    this.loadSavedFilters();

    // Cargar categorías activas
    this.loadActiveCategories();

    // Configurar listeners para cambios de categoría
    this.setupCategoryChangeListener();

    // Recargar cuando cambie el idioma para actualizar los nombres
    this.langSub = this.translateConfigService.onLanguageChange().subscribe(() => {
      this.loadActiveCategories();
      if (this.selectedFilters.category_id || this.selectedFilters.category) {
        this.loadSubcategoriesByCategory(this.selectedFilters.category_id || this.selectedFilters.category);
      }
    });
  }

  private langSub?: Subscription;

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  filters: any[] = [
    {
      id: 'category',
      label: 'FILTERS.CATEGORY',
      placeholder: 'FILTERS.SELECT',
      type: 'select',
      options: [] // Se llenará dinámicamente
    },
    {
      id: 'subcategory',
      label: 'FILTERS.SUBCATEGORY',
      placeholder: 'FILTERS.SELECT_CATEGORY_FIRST',
      type: 'select',
      options: [], // Se llenará dinámicamente
      disabled: true // Inicialmente deshabilitado
    },
    {
      id: 'city',
      label: 'FILTERS.CITY',
      placeholder: 'FILTERS.CITY_PLACEHOLDER',
      type: 'autocomplete',
      options: null
    },
    {
      id: 'location',
      label: 'FILTERS.LOCATION',
      placeholder: 'FILTERS.VIEW_MAP',
      type: 'map',
      options: null
    },
    {
      id: 'startDate',
      label: 'FILTERS.START_DATE',
      placeholder: 'FILTERS.SELECT',
      type: 'date',
      options: null
    },
    {
      id: 'endDate',
      label: 'FILTERS.END_DATE',
      placeholder: 'FILTERS.SELECT',
      type: 'date',
      options: null
    }
  ];

  /**
   * Carga las categorías activas desde el servicio
   */
  loadActiveCategories() {
    this.isLoadingCategories = true;
    this.categoryError = null;

    this.categoriesService.getActiveCategoriesForSelect().subscribe({
      next: (categories) => {
        this.categories = categories;
        this.isLoadingCategories = false;

        // Actualizar las opciones de categoría en el formulario
        this.updateCategoryOptions();

        // Si hay una categoría guardada previamente, cargar sus subcategorías
        if (this.selectedFilters.category) {
          this.loadSubcategoriesByCategory(this.selectedFilters.category);
        }
      },
      error: (error) => {
        this.isLoadingCategories = false;
        this.categoryError = this.translateConfigService.getItemTranslated('CREATE_POST.ERROR_LOADING_CATEGORIES');
      }
    });
  }

  /**
   * Carga las subcategorías activas por ID de categoría
   * @param categoryId ID de la categoría seleccionada
   */
  loadSubcategoriesByCategory(categoryId: number) {
    // Si no hay categoría seleccionada, reiniciar subcategorías
    if (!categoryId) {
      this.subcategories = [];
      this.updateSubcategoryOptions();

      // Limpiar subcategoría seleccionada si existe
      if (this.selectedFilters.subcategory_id || this.selectedFilters.subcategory) {
        delete this.selectedFilters.subcategory_id;
        delete this.selectedFilters.subcategory;
        this.countActiveFilters();
      }

      return;
    }

    // Mostrar spinner de carga
    this.isLoadingSubcategories = true;
    this.subcategoryError = null;

    this.categoriesService.getActiveSubcategoriesForSelect(categoryId).subscribe({
      next: (subcategories) => {
        this.subcategories = subcategories;
        this.isLoadingSubcategories = false;

        // Actualizar las opciones de subcategoría en el formulario
        this.updateSubcategoryOptions();

        // Si no hay subcategorías disponibles, limpiar la selección
        if (subcategories.length === 0) {
          if (this.selectedFilters.subcategory_id || this.selectedFilters.subcategory) {
            delete this.selectedFilters.subcategory_id;
            delete this.selectedFilters.subcategory;
            this.countActiveFilters();
          }
        }
      },
      error: (error) => {
        this.isLoadingSubcategories = false;
        this.subcategoryError = this.translateConfigService.getItemTranslated('CREATE_POST.ERROR_LOADING_SUBCATEGORIES');

        // Limpiar subcategorías en caso de error
        this.subcategories = [];
        this.updateSubcategoryOptions();

        if (this.selectedFilters.subcategory_id || this.selectedFilters.subcategory) {
          delete this.selectedFilters.subcategory_id;
          delete this.selectedFilters.subcategory;
          this.countActiveFilters();
        }
      }
    });
  }

  /**
   * Actualiza las opciones del select de categorías en el filtro
   */
  updateCategoryOptions() {
    const categoryFilter = this.filters.find(filter => filter.id === 'category');
    if (categoryFilter) {
      // Añadir opción "Todos" al inicio
      categoryFilter.options = [
        { label: 'FILTERS.ALL', value: '' },
        ...this.categories.map((category:any) => ({
          label: category.name,
          value: category.id
        }))
      ];
    }
  }

  /**
   * Actualiza las opciones del select de subcategorías en el filtro
   */
  updateSubcategoryOptions() {
    const subcategoryFilter = this.filters.find(filter => filter.id === 'subcategory');
    if (subcategoryFilter) {
      if (this.subcategories.length > 0) {
        // Añadir opción "Todos" al inicio si hay subcategorías
        subcategoryFilter.options = [
          { label: 'FILTERS.ALL', value: '' },
          ...this.subcategories.map((subcategory:any) => ({
            label: subcategory.name,
            value: subcategory.id
          }))
        ];
        subcategoryFilter.placeholder = 'FILTERS.SELECT';
        subcategoryFilter.disabled = false; // Habilitar cuando hay subcategorías
      } else {
        // Si no hay subcategorías disponibles
        subcategoryFilter.options = [];
        subcategoryFilter.placeholder = (this.selectedFilters.category_id || this.selectedFilters.category) ?
          'FILTERS.NO_SUBCATEGORIES' :
          'FILTERS.SELECT_CATEGORY_FIRST';
        subcategoryFilter.disabled = !(this.selectedFilters.category_id || this.selectedFilters.category); // Deshabilitar si no hay categoría
      }
    }
  }

  /**
   * Configura el listener para cambios en la categoría
   */
  setupCategoryChangeListener() {
    // Este método se ejecutará cuando se actualice un filtro
    // La lógica se manejará en updateFilter
  }

  // Cargar filtros guardados
  loadSavedFilters() {
    const savedFilters = this.storage.get(StorageKeys.ACTIVE_FILTERS);
    if (savedFilters) {
      this.selectedFilters = JSON.parse(savedFilters);
      this.countActiveFilters();
    }
  }

  // Método para actualizar un filtro cuando cambia
  updateFilter(event: {id: string, value: any}) {


    this.selectedFilters[event.id] = event.value;
    // Mantener también claves normalizadas category_id / subcategory_id
    if (event.id === 'category') {
      if (event.value) this.selectedFilters['category_id'] = event.value; else delete this.selectedFilters['category_id'];
    }
    if (event.id === 'subcategory') {
      if (event.value) this.selectedFilters['subcategory_id'] = event.value; else delete this.selectedFilters['subcategory_id'];
    }

    // Si se cambió la categoría, recargar subcategorías
    if (event.id === 'category') {
      // Limpiar subcategoría seleccionada cuando cambia la categoría
      if (this.selectedFilters.subcategory_id || this.selectedFilters.subcategory) {
        delete this.selectedFilters.subcategory_id;
        delete this.selectedFilters.subcategory;

        // Resetear el componente de subcategoría
        const subcategoryInput = this.filterInputs.find(input =>
          input.input.id === 'subcategory'
        );
        if (subcategoryInput) {
          subcategoryInput.reset();
        }
      }

      // Cargar nuevas subcategorías
      const categoryId = event.value ? Number(event.value) : null;
      if (categoryId) {
        this.loadSubcategoriesByCategory(categoryId);
      } else {
        // Si se selecciona "Todos", limpiar subcategorías
        this.subcategories = [];
        this.updateSubcategoryOptions();
      }
    }

    this.countActiveFilters();
  }

  // Contar filtros activos
  countActiveFilters() {
    this.activeFiltersCount = Object.keys(this.selectedFilters).filter(key => {
      const value = this.selectedFilters[key];
      if (key === 'city') {
        return value && value.selected; // cuenta solo si hay selección válida
      }
      return value !== null && value !== undefined && value !== '';
    }).length;
  }

  // Aplicar los filtros seleccionados
  applyFilters() {
    if (this.selectedFilters.city && this.selectedFilters.city.input && !this.selectedFilters.city.selected) {
      this.showCitySelectionToast();
      return;
    }
    // Normalizar claves legacy antes de persistir/emitir
    const normalized: any = { ...this.selectedFilters };
    if (normalized.category && !normalized.category_id) {
      normalized.category_id = normalized.category;
      delete normalized.category;
    }
    if (normalized.subcategory && !normalized.subcategory_id) {
      normalized.subcategory_id = normalized.subcategory;
      delete normalized.subcategory;
    }
    this.storage.setJSON(StorageKeys.ACTIVE_FILTERS, normalized);
    this.filtersApplied.emit({
      filters: normalized,
      count: this.activeFiltersCount
    });

    // Emitir un evento personalizado para que listing.page lo capture
    const event = new CustomEvent('filtersChanged', {
      detail: {
        filters: normalized,
        count: this.activeFiltersCount
      },
      bubbles: true
    });
    document.dispatchEvent(event);

    // Cerrar el menú
    this.toggleFilters();
  }

  // Limpiar todos los filtros
  clearFilters() {
    this.selectedFilters = {};
    this.storage.remove(StorageKeys.ACTIVE_FILTERS);
    this.activeFiltersCount = 0;

    // Restablecer manualmente cada componente filter-input
    if (this.filterInputs) {
      this.filterInputs.forEach(input => {
        // Usar el método público reset
        input.reset();
      });
    }

    // Resetear subcategorías cuando se limpian los filtros
    this.subcategories = [];
    this.updateSubcategoryOptions();

    // Emitir evento de cambio de filtros con valores vacíos
    const event = new CustomEvent('filtersChanged', {
      detail: {
        filters: {},
        count: 0
      },
      bubbles: true
    });
    document.dispatchEvent(event);

    // Cerrar el menú
    this.toggleFilters();
  }

  // Obtener valor actual de un filtro
  getFilterValue(filterId: string): any {
    return this.selectedFilters[filterId] || null;
  }

  toggleFilters() {
    this.menuCtrl.toggle('filters');
  }

  private async showCitySelectionToast() {
    const msg = this.translateConfigService.getItemTranslated('FILTERS.CITY_SELECT_SUGGESTION') || 'Seleccione una ciudad de las sugerencias';
    const toast = await this.toastController.create({
      message: msg,
      duration: 2500,
      color: 'warning',
      position: 'bottom'
    });
    await toast.present();
  }
}
